import React, { useEffect } from 'react';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import { Observer } from 'mobx-react';
import { css } from '@emotion/core';
import { QueryParamProvider } from 'use-query-params';
import { io } from 'socket.io-client';

import { useRootStore } from '../stores/BridgeProvider';
import AuthRoute from './util/AuthRoute';
import SettingsAgnosticRoute from './util/SettingsAgnosticRoute';
import storageService from '../services/storage.service';
import App from '../components/App';
import Callback from '../components/callbacks/Callback';
import Dashboard from '../components/Dashboard';
import Login from '../components/Login';
import SelectOrg from '../components/organization/SelectOrg';
import LinkOrg from '../components/organization/LinkOrg';
import XeroCallback from '../components/callbacks/XeroCallback';
import XeroRefreshCallback from '../components/callbacks/XeroRefreshCallback';
import Spinner from '../components/util/Spinner';
import CreateOrg from '../components/organization/CreateOrg';
import Profile from '../components/account/Profile';
import AccountSettings from '../components/account/AccountSettings';
import InviteUser from '../components/invites/InviteUser';
import PendingInvites from '../components/invites/PendingInvites';
import InviteInfo from '../components/invites/InviteInfo';
import Drag from '../components/Drag';
import Map from '../components/Map';
import ReferralLink from '../components/account/ReferralLink';
import Vouchers from '../components/vouchers/Vouchers';
import Contacts from '../components/contacts/Contacts';
import OrgUserInfo from '../components/users/OrgUserInfo';
import VoucherInfo from '../components/vouchers/VoucherInfo';
import ContactInfo from '../components/contacts/ContactInfo';
import NewDeal from '../components/deal/NewDeal';
import DealDashboard from '../components/deal/DealDashboard';
import NewDealTransaction from '../components/deal/NewDealTransaction';
import DealMapper from '../components/dealMapper';
import NewContact from '../components/contacts/NewContact';
import NewVoucher from '../components/vouchers/NewVoucher';
import OrgPage from '../components/organization/OrgPage';
import EditDeal from '../components/deal/EditDeal';
import EditDealTransaction from '../components/deal/EditDealTransaction';
import Payments from '../components/payments/Payments';
import NewPayment from '../components/payments/NewPayment';
import PaymentInfo from '../components/payments/PaymentInfo';
import TransactionCategories from '../components/transactionCategories/TransactionCategories';
import TransactionCategoryInfo from '../components/transactionCategories/TransactionCategoryInfo';
import NewTransactionCategory from '../components/transactionCategories/NewTransactionCategory';
import Accounts from '../components/accounts/Accounts';
import AccountInfo from '../components/accounts/AccountInfo';
import NewAccount from '../components/accounts/NewAccount';
import ExtSysMapper from '../components/organization/ExtSysMapper';
import TranCategoryMapper from '../components/organization/TranCategoryMapper';
import NewVoucherTransaction from '../components/vouchers/NewVoucherTransaction';
import EditVoucherTransaction from '../components/vouchers/EditVoucherTransaction';
import NewDealTemplate from '../components/dealTemplate/NewDealTemplate';
import SelectTemplate from '../components/deal/NewDeal/SelectTemplate';
import Deals from '../components/deal/Deals';
import DealTemplates from '../components/dealTemplate/DealTemplates';
import DealTemplate from '../components/dealTemplate/DealTemplate';
import CsvImport from '../components/CsvImport';
import NewPaymentType from '../components/paymentTypes/NewPaymentType';
import PaymentTypes from '../components/paymentTypes/PaymentTypes';
import PaymentTypeInfo from '../components/paymentTypes/PaymentTypeInfo';
import UsersPage from '../components/users/UsersPage';
import AdvancedUser from '../components/users/AdvancedUser';
import Roles from '../components/roles';
import RoleInfo from '../components/roles/RoleInfo';
import NewRole from '../components/roles/NewRole';
import Reports from '../components/reports/Reports';
import Report from '../components/reports/Report';
import ContactAgents from '../components/organization/ContactAgents';
import EmailConfirmation from '../components/EmailConfirmation';
import SyncInProgress from '../components/organization/SyncInProgress';
import ExternalSystemSettings from '../components/organization/ExternalSystemSettings.js';
import { SnackBarProvider } from '../alerts/SnackBarProvider';
import Checklist from '../components/onboarding/checklist/Checklist';
import SelectNewDealType from '../components/deal/NewDeal/SelectNewDealType';
import SelectNewDealTemplateType from '../components/dealTemplate/SelectNewDealTemplateType';
import TemplateWizard from '../components/dealTemplate/TemplateWizard';
import IntegrationErrors from '../components/organization/IntegrationErrors';
import IntegrationHistory from '../components/organization/IntegrationHistory';
import XeroAuth from '../components/callbacks/XeroAuth';
import DirectSignup from '../components/callbacks/DirectSignup';
import DirectLogin from '../components/callbacks/DirectLogin';

const SpinStyle = css`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const containerStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
};

const CheckComp = () => <div>{process.env.REACT_APP_DOMAIN}</div>;

const Router = () => {
  const { authStore, orgStore } = useRootStore();
  useEffect(() => {
    const { renewSession, isAuthenticated, stopAuthenticating } = authStore;
    if (!isAuthenticated && storageService.getItem('isLoggedIn')) {
      renewSession();
    } else {
      stopAuthenticating();
    }
  }, []);

  return (
    <Observer
      render={() => {
        const { userId } = orgStore;
        const {
          authenticating,
          accessToken,
          addNotification,
          setNotifications,
        } = authStore;

        useEffect(() => {
          if (accessToken && userId) {
            try {
              const socket = io(`${process.env.REACT_APP_API}`, {
                // extraHeaders: { Authorization: `Bearer ${accessToken}` },
                reconnectionDelay: 1000,
                reconnection: true,
                reconnectionAttempts: 10,
                transports: ['websocket'],
                agent: false,
                upgrade: false,
                rejectUnauthorized: false,
                query: { userId },
                auth: { token: `Bearer ${accessToken}` },
              });
              socket.on('Notification', data => {
                console.log(data);
                const parsedData = JSON.parse(data.message);
                const { initial, notifications: n } = parsedData;

                if (!n) {
                  console.log(data);
                } else if (initial) {
                  setNotifications(n);
                } else {
                  addNotification(n);
                }

                // setResponse(data);
              });
              return () => socket.disconnect();
            } catch (e) {
              console.log('SOCKET ERROR', e);
            }
          }
        }, [accessToken, userId]);

        if (authenticating) {
          return (
            <div css={SpinStyle}>
              <Spinner />
            </div>
          );
        }

        return (
          <div style={containerStyle}>
            <BrowserRouter>
              <QueryParamProvider ReactRouterRoute={Route}>
                <SnackBarProvider>
                  <Switch>
                    <Route exact path="/" component={App} />
                    <Route exact path="/login" component={Login} />
                    <Route exact path="/check" component={CheckComp} />
                    <Route exact path="/signup/:referralID" component={Login} />
                    <Route
                      exact
                      path="/xero-callback"
                      component={XeroCallback}
                    />
                    <Route exact path="/xero-auth" component={XeroAuth} />
                    <Route exact path="/xero-login" component={XeroAuth} />
                    <Route
                      exact
                      path="/initial-signup"
                      component={DirectSignup}
                    />
                    <Route exact path="/direct-login" component={DirectLogin} />
                    <Route
                      exact
                      path="/xero-refresh-callback"
                      component={XeroRefreshCallback}
                    />
                    <Route exact path="/callback" component={Callback} />
                    <Route exact path="/drag" component={Drag} />
                    <Route exact path="/map" component={Map} />
                    <Route
                      path="/email-confirmation"
                      component={EmailConfirmation}
                    />
                    <AuthRoute exact path="/dashboard" component={Dashboard} />
                    <AuthRoute exact path="/organization" component={OrgPage} />
                    <AuthRoute
                      exact
                      path="/organization/agent-contacts"
                      component={ContactAgents}
                    />
                    <AuthRoute
                      exact
                      path="/organization/:tab"
                      component={OrgPage}
                    />
                    <AuthRoute
                      exact
                      path="/sync-in-progress"
                      component={SyncInProgress}
                    />
                    <AuthRoute
                      exact
                      path="/external-system-settings"
                      component={ExternalSystemSettings}
                    />
                    <AuthRoute
                      exact
                      path="/integration-errors"
                      component={IntegrationErrors}
                    />
                    <AuthRoute
                      exact
                      path="/integration-history"
                      component={IntegrationHistory}
                    />
                    <AuthRoute exact path="/import" component={CsvImport} />
                    <AuthRoute
                      exact
                      path="/ext-sys-mapper"
                      component={ExtSysMapper}
                      permission="EXTSYS_M"
                    />
                    <AuthRoute
                      exact
                      path="/transaction-category-mapper"
                      component={TranCategoryMapper}
                      permission="EXTCODEMAP_M"
                    />
                    <SettingsAgnosticRoute
                      exact
                      path="/select-organization"
                      component={SelectOrg}
                    />
                    <SettingsAgnosticRoute
                      exact
                      path="/create-organization"
                      component={CreateOrg}
                    />
                    <SettingsAgnosticRoute
                      exact
                      path="/link-organization"
                      component={LinkOrg}
                    />
                    <AuthRoute
                      exact
                      path="/user-management/users/:userID"
                      component={OrgUserInfo}
                      permission="USER_M"
                    />
                    <AuthRoute
                      exact
                      path="/user-management"
                      component={UsersPage}
                      permission="USER_M"
                    />
                    <AuthRoute
                      exact
                      path="/advanced-settings"
                      component={AdvancedUser}
                      permission="USER_M"
                    />
                    <AuthRoute
                      exact
                      path="/roles"
                      component={Roles}
                      permission="USER_M"
                    />
                    <AuthRoute
                      exact
                      path="/roles/:orgRoleId"
                      component={RoleInfo}
                      permission="USER_M"
                    />
                    <AuthRoute
                      exact
                      path="/role/new"
                      component={NewRole}
                      permission="USER_M"
                    />
                    <AuthRoute
                      exact
                      path="/user-management/:tab"
                      component={UsersPage}
                      permission="USER_M"
                    />
                    <AuthRoute
                      exact
                      path="/invite-user"
                      component={InviteUser}
                    />
                    <AuthRoute
                      exact
                      path="/pending-invites"
                      component={PendingInvites}
                    />
                    <AuthRoute
                      exact
                      path="/invite/:inviteID"
                      component={InviteInfo}
                    />
                    <AuthRoute
                      exact
                      path="/referrals"
                      component={ReferralLink}
                    />
                    <AuthRoute exact path="/vouchers" component={Vouchers} />
                    <AuthRoute exact path="/vouchers/new">
                      <NewVoucher type="voucher" />
                    </AuthRoute>
                    <AuthRoute
                      exact
                      path="/deal-mapper"
                      component={DealMapper}
                    />
                    <AuthRoute
                      exact
                      path="/vouchers/:voucherID"
                      component={VoucherInfo}
                    />
                    <AuthRoute
                      exact
                      path="/vouchers/:voucherID/new-voucher-transaction"
                      component={NewVoucherTransaction}
                    />
                    <AuthRoute
                      exact
                      path="/vouchers/voucher-transaction/:transID/edit"
                      component={EditVoucherTransaction}
                    />
                    <AuthRoute
                      exact
                      path="/contacts"
                      component={Contacts}
                      permission="CONTACT_S"
                    />
                    <AuthRoute
                      exact
                      path="/contacts/new"
                      component={NewContact}
                      permission="CONTACT_I"
                    />
                    <AuthRoute
                      exact
                      path="/contacts/:contactID"
                      component={ContactInfo}
                      permission="CONTACT_M"
                    />
                    <AuthRoute exact path="/payments" component={Payments} />
                    <AuthRoute
                      exact
                      path="/payments/new"
                      component={NewPayment}
                      permission="PAY_I"
                    />
                    <AuthRoute
                      exact
                      path="/payments/:paymentID"
                      component={PaymentInfo}
                    />
                    <AuthRoute
                      exact
                      path="/account-settings"
                      component={AccountSettings}
                    />
                    <AuthRoute
                      exact
                      path="/transaction-categories"
                      component={TransactionCategories}
                    />
                    <AuthRoute
                      exact
                      path="/transaction-categories/new"
                      component={NewTransactionCategory}
                      permission="TRANCAT_M"
                    />
                    <AuthRoute
                      exact
                      path="/transaction-categories/:tranCategoryId"
                      component={TransactionCategoryInfo}
                      permission="TRANCAT_M"
                    />
                    <AuthRoute exact path="/accounts" component={Accounts} />
                    <AuthRoute
                      exact
                      path="/accounts/new"
                      component={NewAccount}
                      permission="COA_M"
                    />
                    <AuthRoute
                      exact
                      path="/accounts/:accountId"
                      component={AccountInfo}
                      permission="COA_M"
                    />
                    <AuthRoute path="/profile" component={Profile} />
                    <AuthRoute
                      exact
                      path="/deals/new"
                      component={SelectNewDealType}
                      permission="DEAL_I"
                    />
                    <AuthRoute
                      exact
                      path="/deals/new-manual"
                      component={NewDeal}
                      permission="DEAL_I"
                    />
                    <AuthRoute
                      exact
                      path="/new-deal-from-template"
                      component={SelectTemplate}
                      permission="DEAL_I"
                    />
                    <AuthRoute
                      exact
                      path="/deal-template/:templateId"
                      component={DealTemplate}
                      permission="DEALTEMP_M"
                    />
                    <AuthRoute
                      exact
                      path="/deal-templates/new"
                      component={SelectNewDealTemplateType}
                      permission="DEALTEMP_M"
                    />
                    <AuthRoute
                      exact
                      path="/deal-templates/wizard"
                      component={TemplateWizard}
                      permission="DEALTEMP_M"
                    />
                    <AuthRoute
                      exact
                      path="/deal-templates/new-advanced"
                      component={NewDealTemplate}
                      permission="DEALTEMP_M"
                    />
                    <AuthRoute
                      exact
                      path="/deal-templates"
                      component={DealTemplates}
                      permission="DEALTEMP_M"
                    />
                    <AuthRoute exact path="/deals" component={Deals} />
                    <AuthRoute
                      exact
                      path="/deal/:dealID/edit"
                      component={EditDeal}
                    />
                    <AuthRoute
                      exact
                      path="/deal/:dealID"
                      component={DealDashboard}
                    />
                    <AuthRoute
                      exact
                      path="/deal-transaction/:transID/edit"
                      component={EditDealTransaction}
                    />
                    <AuthRoute
                      exact
                      path="/deal/:dealID/new-deal-transaction"
                      component={NewDealTransaction}
                    />
                    <AuthRoute
                      exact
                      path="/payment-types"
                      component={PaymentTypes}
                    />
                    <AuthRoute
                      exact
                      path="/payment-types/new"
                      permission="PAYMENTTYPE_M"
                      component={NewPaymentType}
                    />
                    <AuthRoute
                      exact
                      path="/payment-types/:PaymentTypeId"
                      component={PaymentTypeInfo}
                      permission="PAYMENTTYPE_M"
                    />
                    <AuthRoute exact path="/reports" component={Reports} />
                    <AuthRoute
                      exact
                      path="/report/new/:reportType"
                      component={Report}
                    />
                    <AuthRoute
                      exact
                      path="/report/invoice"
                      component={Report}
                    />
                    <AuthRoute
                      exact
                      path="/report/:reportID"
                      component={Report}
                    />
                    <AuthRoute
                      exact
                      path="/onboarding/checklist"
                      component={Checklist}
                    />
                    {/* <AuthRoute
                      exact
                      path="/test-component"
                      component={DealTranTableForm}
                    /> */}
                    <Redirect to="/" />
                  </Switch>
                </SnackBarProvider>
              </QueryParamProvider>
            </BrowserRouter>
          </div>
        );
      }}
    />
  );
};
export default Router;
